import React, { useState, useMemo } from 'react';

const PhraseSentimentAnalysis = ({ category, reviews }) => {
  const [selectedSentiment, setSelectedSentiment] = useState('all'); // 'all', 'positive', 'negative', 'neutral'
  
  // Extract relevant reviews for this category
  const categoryReviews = useMemo(() => {
    if (!reviews || !category) return [];
    
    return reviews
      .filter(review => review.categorySentiments.some(cs => cs.category === category))
      .map(review => {
        // Find the sentiment for this specific category in this review
        const categorySentiment = review.categorySentiments.find(cs => cs.category === category)?.sentiment || 'Neutral';
        return {
          ...review,
          categorySentiment
        };
      })
      .filter(review => 
        selectedSentiment === 'all' || 
        review.categorySentiment.toLowerCase() === selectedSentiment
      )
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [reviews, category, selectedSentiment]);
  
  // Extract and analyze key phrases
  const keyPhrases = useMemo(() => {
    if (!categoryReviews.length) return [];
    
    // In a real implementation, this would use NLP to extract meaningful phrases
    // For the demo, we'll use a simple approach with random highlights
    
    // Common phrases associated with different categories
    const phraseDictionary = {
      'Product Quality': [
        { text: 'excellent quality', sentiment: 'Positive' },
        { text: 'good build', sentiment: 'Positive' },
        { text: 'durable product', sentiment: 'Positive' },
        { text: 'premium feel', sentiment: 'Positive' },
        { text: 'poor quality', sentiment: 'Negative' },
        { text: 'breaks easily', sentiment: 'Negative' },
        { text: 'not worth the price', sentiment: 'Negative' },
        { text: 'decent quality', sentiment: 'Neutral' }
      ],
      'Customer Service': [
        { text: 'helpful staff', sentiment: 'Positive' },
        { text: 'quick response', sentiment: 'Positive' },
        { text: 'resolved my issue', sentiment: 'Positive' },
        { text: 'poor service', sentiment: 'Negative' },
        { text: 'never responded', sentiment: 'Negative' },
        { text: 'rude representatives', sentiment: 'Negative' },
        { text: 'average service', sentiment: 'Neutral' }
      ],
      'Delivery & Shipping': [
        { text: 'fast delivery', sentiment: 'Positive' },
        { text: 'arrived on time', sentiment: 'Positive' },
        { text: 'well packaged', sentiment: 'Positive' },
        { text: 'delayed shipment', sentiment: 'Negative' },
        { text: 'damaged packaging', sentiment: 'Negative' },
        { text: 'standard delivery', sentiment: 'Neutral' }
      ],
      'App Experience': [
        { text: 'user friendly', sentiment: 'Positive' },
        { text: 'intuitive interface', sentiment: 'Positive' },
        { text: 'easy to navigate', sentiment: 'Positive' },
        { text: 'app crashes', sentiment: 'Negative' },
        { text: 'slow loading', sentiment: 'Negative' },
        { text: 'basic functionality', sentiment: 'Neutral' }
      ],
      'Price & Value': [
        { text: 'great value', sentiment: 'Positive' },
        { text: 'worth every penny', sentiment: 'Positive' },
        { text: 'reasonable price', sentiment: 'Positive' },
        { text: 'overpriced', sentiment: 'Negative' },
        { text: 'not worth it', sentiment: 'Negative' },
        { text: 'average value', sentiment: 'Neutral' }
      ]
    };
    
    // Get phrases for this category or use a default set
    const relevantPhrases = phraseDictionary[category] || [
      { text: 'excellent', sentiment: 'Positive' },
      { text: 'good', sentiment: 'Positive' },
      { text: 'poor', sentiment: 'Negative' },
      { text: 'bad', sentiment: 'Negative' },
      { text: 'average', sentiment: 'Neutral' }
    ];
    
    // Count occurrences in reviews
    const phraseStats = relevantPhrases.map(phrase => {
      const matchingReviews = categoryReviews.filter(review => 
        review.reviewText.toLowerCase().includes(phrase.text.toLowerCase())
      );
      
      return {
        ...phrase,
        count: matchingReviews.length,
        percentage: Math.round((matchingReviews.length / categoryReviews.length) * 100) || 0
      };
    });
    
    // Sort by frequency
    return phraseStats
      .filter(phrase => phrase.count > 0)
      .sort((a, b) => b.count - a.count);
  }, [categoryReviews, category]);
  
  // Highlight phrases in review text
  const highlightPhrases = (text, phrases) => {
    if (!phrases || !phrases.length) return text;
    
    let highlightedText = text;
    const lowerText = text.toLowerCase();
    
    // Sort phrases by length (descending) to avoid highlighting parts of longer phrases
    const sortedPhrases = [...phrases].sort((a, b) => b.text.length - a.text.length);
    
    sortedPhrases.forEach(phrase => {
      const phraseText = phrase.text.toLowerCase();
      let lastIndex = 0;
      let modified = '';
      
      while (lastIndex < lowerText.length) {
        const index = lowerText.indexOf(phraseText, lastIndex);
        if (index === -1) {
          modified += highlightedText.slice(lastIndex);
          break;
        }
        
        modified += highlightedText.slice(lastIndex, index);
        
        // Get the actual cased version from the original text
        const actualPhrase = highlightedText.slice(index, index + phraseText.length);
        
        const sentimentClass = 
          phrase.sentiment === 'Positive' ? 'bg-green-100 text-green-800' :
          phrase.sentiment === 'Negative' ? 'bg-red-100 text-red-800' :
          'bg-gray-100 text-gray-800';
        
        modified += `<span class="${sentimentClass} rounded px-1">${actualPhrase}</span>`;
        
        lastIndex = index + phraseText.length;
      }
      
      highlightedText = modified;
    });
    
    return highlightedText;
  };
  
  // Get sentiment color classes
  const getSentimentClass = (sentiment) => {
    switch (sentiment.toLowerCase()) {
      case 'positive': return 'bg-green-50 border-green-200';
      case 'negative': return 'bg-red-50 border-red-200';
      case 'neutral': return 'bg-gray-50 border-gray-200';
      default: return 'bg-gray-50 border-gray-200';
    }
  };
  
  return (
    <div className="">
      <h3 className="text-md font-medium text-gray-900 mb-2">Phrase Sentiment Analysis: {category}</h3>
      
      {/* Sentiment filter tabs */}
      <div className="flex border-b border-gray-200 mb-5">
        <button
          className={`mr-6 py-2 px-1 text-sm font-medium ${
            selectedSentiment === 'all'
              ? 'border-b-2 border-blue-500 text-blue-600'
              : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
          onClick={() => setSelectedSentiment('all')}
        >
          All Reviews
        </button>
        <button
          className={`mr-6 py-2 px-1 text-sm font-medium ${
            selectedSentiment === 'positive'
              ? 'border-b-2 border-green-500 text-green-600'
              : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
          onClick={() => setSelectedSentiment('positive')}
        >
          Positive
        </button>
        <button
          className={`mr-6 py-2 px-1 text-sm font-medium ${
            selectedSentiment === 'negative'
              ? 'border-b-2 border-red-500 text-red-600'
              : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
          onClick={() => setSelectedSentiment('negative')}
        >
          Negative
        </button>
        <button
          className={`py-2 px-1 text-sm font-medium ${
            selectedSentiment === 'neutral'
              ? 'border-b-2 border-gray-500 text-gray-600'
              : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
          }`}
          onClick={() => setSelectedSentiment('neutral')}
        >
          Neutral
        </button>
      </div>
      
      {/* Key phrases grid */}
      <div className="mb-6">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Key Phrases</h4>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {keyPhrases.slice(0, 8).map((phrase, index) => (
            <div
              key={index}
              className={`px-3 py-2 rounded-lg border ${
                phrase.sentiment === 'Positive' ? 'bg-green-50 border-green-200' :
                phrase.sentiment === 'Negative' ? 'bg-red-50 border-red-200' :
                'bg-gray-50 border-gray-200'
              }`}
            >
              <div className="text-sm font-medium">"{phrase.text}"</div>
              <div className="text-xs text-gray-500 mt-1">
                {phrase.count} mentions ({phrase.percentage}%)
              </div>
            </div>
          ))}
          
          {keyPhrases.length === 0 && (
            <div className="col-span-full text-sm text-gray-500 py-6 text-center">
              No key phrases found for this category and filter
            </div>
          )}
        </div>
      </div>
      
      {/* Review samples with highlighted phrases */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 mb-3">Review Samples</h4>
        <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
          {categoryReviews.slice(0, 5).map((review, index) => (
            <div 
              key={index}
              className={`p-4 rounded-lg border ${getSentimentClass(review.categorySentiment)}`}
            >
              <div className="flex justify-between mb-2">
                <span className="text-sm font-medium">{review.userName}</span>
                <span className="text-xs text-gray-500">
                  {new Date(review.date).toLocaleDateString()}
                </span>
              </div>
              <div 
                className="text-sm text-gray-700"
                dangerouslySetInnerHTML={{ 
                  __html: highlightPhrases(review.reviewText, keyPhrases) 
                }}
              ></div>
              <div className="mt-2 flex items-center">
                <div className="flex">
                  {Array(5).fill(0).map((_, i) => (
                    <svg
                      key={i}
                      className={`w-4 h-4 ${i < review.rating ? 'text-yellow-400' : 'text-gray-300'}`}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <span className="ml-2 text-xs text-gray-500">
                  via {review.source}
                </span>
              </div>
            </div>
          ))}
          
          {categoryReviews.length === 0 && (
            <div className="text-sm text-gray-500 py-6 text-center">
              No reviews found for this category and filter
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PhraseSentimentAnalysis;